import React, { FC, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

import { closeModal } from '../../Store/modalSlice';
import { RootState } from '../../Store/store';
import ImageAnalyzeModal from '../Image/ImageAnalyzeModal';
import MultipleImageUpload from '../Image/MultipleImageUpload';
import ProjectCreateForm from '../Project/ProjectCreateForm';
import ReportPreview from '../Report/ReportPreview';
import MinimizedUploadProgress from '../Image/MinimizedUploadProgress';
import { getImageByProject } from '../../Store/imageQueueSlice';
import SubscriptionComparisonModal from '../Subscription/SubscriptionComparisonModal';
import InviteJoinConfirmModal from '../Invite/InviteJoinConfirmModal';
import InviteErrorModal from '../Invite/InviteErrorModal';
import InviteSendModal from '../Invite/InviteSendModal';

const Modal: FC = () => {
  const dispatch = useDispatch();
  const { isOpen, type, width, dismissible, props: modalProps } = useSelector((state: RootState) => state.modal);
  const [isMinimized, setIsMinimized] = useState(false);
  const imageQueue = useSelector((state: RootState) => getImageByProject(state, modalProps.projectId || ""));
  const hasUploadsInProgress = imageQueue.inprog.length > 0;

  const modalWidth = useMemo(() => {
    if (width === "sm") return "w-11/12 sm:max-w-sm";
    if (width === "md") return "w-11/12 sm:max-w-md";
    if (width === "lg") return "w-11/12 sm:max-w-lg";
    if (width === "xl") return "w-11/12 sm:max-w-5xl";
    if (width === "full") return "w-11/12 sm:max-w-6xl";
  }, [width]);

  if (!isOpen || !type) return null;

  let Component;
  let title = "";
  let showHeader = true;

  if (type === "project") {
    title = "Create Project";
    Component = ProjectCreateForm;
  } else if (type === "image") {
    title = "Upload Image";
    Component = MultipleImageUpload;
  } else if (type === "analyze") {
    title = "Analyze Image";
    Component = ImageAnalyzeModal;
  } else if (type === "report") {
    title = "Create Report";
    Component = ReportPreview;
  } else if (type === "subscription") {
    title = "Subscription Update";
    showHeader = false; // Hide header for subscription modal
    Component = () => (
      <SubscriptionComparisonModal
        currentSubscription={modalProps.currentSubscription}
        newTier={modalProps.newTier}
        isYearly={modalProps.isYearly}
        isForcedCheckout={modalProps.isForcedCheckout}
        closeModal={() => dispatch(closeModal())}
      />
    );
  } else if (type === "invite-join-confirm") {
    title = "Join Tenant";
    Component = () => (
      <InviteJoinConfirmModal
        tenantName={modalProps.tenantName}
        closeModal={() => dispatch(closeModal())}
      />
    );
  } else if (type === "invite-error") {
    title = "Invitation Issue";
    Component = () => (
      <InviteErrorModal
        reason={modalProps.reason}
        closeModal={() => dispatch(closeModal())}
      />
    );
  } else if (type === "invite-send") {
    title = "Invite Team Member";
    Component = InviteSendModal;
  }

  const handleModalClose = () => {
    // Don't allow closing if modal is not dismissible
    if (!dismissible) {
      return;
    }
    
    if (type === "image" && hasUploadsInProgress) {
      setIsMinimized(true);
    } else {
      dispatch(closeModal());
    }
  };

  const handleMaximize = () => {
    setIsMinimized(false);
  };

  if (isMinimized && type === "image") {
    return (
      <MinimizedUploadProgress 
        projectId={modalProps.projectId || ""} 
        onMaximize={handleMaximize} 
      />
    );
  }

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[999]"
        onClick={handleModalClose}
        aria-hidden="true"
      />
      {/* Modal container */}
      <div
        className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center p-0 sm:p-6"
        onClick={handleModalClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-modal-title"
      >
        <div
          className={`modal-box max-w-full p-0 w-11/12 ${modalWidth}`}
          onClick={(e) => e.stopPropagation()}
        >
          {showHeader && (
            <div className="border-b border-gray-200 dark:border-neutral-700 px-6 py-4 relative">
              {title && <h3 id="app-modal-title" className="text-lg font-bold">{title}</h3>}
              {type === "image" && hasUploadsInProgress && (
                <button 
                  onClick={() => setIsMinimized(true)}
                  className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  aria-label="Minimize upload progress"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
          )}
          <div className={`${showHeader ? 'px-6 py-4' : 'p-6'}`}>
            {Component && <Component projectId={modalProps.projectId || ""} closeModal={handleModalClose} clearSelectionOnClose={modalProps.clearSelectionOnClose} />}
          </div>
        </div>
      </div>
    </>,
    document.getElementById("modal-root")!,
  );
};

export default Modal;
